/**********************************************************************
// @@@ START COPYRIGHT @@@
//        AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
//        Copyright 2018 - 2019
//        Avi Solar Energy Pvt Ltd.
//        Protected as an unpublished work.
//  The computer program listings, specifications and documentation
//  herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
//  and shall not be reproduced, copied, disclosed, or used in whole or in part for
//  any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <div>
    <v-layout row align-start wrap>
      <v-flex xs12 sm12 >
        <DataCard :boxDataSet="boxData"></DataCard>
      </v-flex>
      <v-flex xs12 sm12 >
        <Card class="card--height">
          <Toolbar>
            <div style="display: flex">
              <div class="mr-4 subheading">{{ $t('Weather Station') }} </div>
              <div class="body-1">
                [ {{ selected_ws ? selected_ws.text : '' }} ]
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="body-1">
              {{ `${show_date[0]} - ${show_date[1]}` }}
            </div>
          </Toolbar>
          <Card class="padding-2">
            <v-layout row wrap>
              <v-flex xs12 text-xs-right>
                <Toggle :modelValue="currentView" :theme="theme" @toggleValueChange="viewChangeHandler">
                  <v-btn flat value="graph">
                    <span>{{ $t('Graph') }}</span>
                    <v-icon>bar_chart</v-icon>
                  </v-btn>
                  <v-btn flat value="table">
                    <span>{{ $t('Table') }}</span>
                    <v-icon>table_chart</v-icon>
                  </v-btn>
                </Toggle>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
            <v-flex v-show="currentView === 'graph'" :class="flexSize" mt-3 v-for="(data, key) in computedGraphData" :key="key">
              <Graph :title="key" :theme="theme" :series="data" :xAxis="axis" />
            </v-flex>
            <v-flex v-if="currentView === 'table'" sm12>
              <Table class="ws-datatable" :date="full_date" :wsId="selected_ws.value" :plantId="plantId">
              </Table>
            </v-flex>
          </v-layout>
          </Card>
        </Card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import WSApi from "@/services/WeatherStation"
import { mixin, mapGettersMixin } from '@/util/mixin/index'
import { mapGetters, mapMutations } from 'vuex'

import Card from '@/components/common/Cards.vue'
const Table = () => import('@/components/weather_station/DataTable.vue')
const Graph = () => import('@/components/weather_station/Graph.vue')
const RangeDatePicker = () => import('@/components/utility/RangeDatePicker')
const moment = require('moment')
const momentTz = require('moment-timezone')

const DataCard = () => import('@/components/weather_station/DataCard')

export default {
  mixins: [ mixin, mapGettersMixin ],
  components: {
    Table,
    Graph,
    RangeDatePicker,
    Card,
    DataCard
  },
  props: {
    changesFromFilter: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {
      enableWatch: false,
      plantId: '',
      axis: [],
      currentView: 'graph',
      graphData: {},
      boxData: {},
      wsList: [],
      activeFilter: {}
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getActivePlantTimezone']),
    filter() {
      return [
        {
          id: "Date",
          type: "datePicker",
          title: "Date",
        },
        {
          id: "wsList",
          type: "select",
          title: "Select WS",
          value: this.wsList,
        },
      ]
    },
    full_date() {
      return this.activeFilter && this.activeFilter.Date ? this.activeFilter.Date : {
        from: momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD'),
        to: momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD')
      }
    },
    show_date() {
      return [moment(this.full_date.from).format('YYYY/M/D'), moment(this.full_date.to).format('YYYY/M/D')]
    },
    selected_ws() {
      return this.activeFilter.wsList ? this.activeFilter.wsList : []
    },
    flexSize() {
      const length = Object.keys(this.graphData).length
      if (length === 0) { return 'xs12' }
      if (length >= 3) { return 'sm4' }
      return 'sm' + 12 / length
    },
    computedGraphData() {
      const cloneGraphData = _.cloneDeep(this.graphData)
      const tempObj = {}
      Object.keys(cloneGraphData).forEach(key => {
        tempObj[key] = cloneGraphData[key].map(paramObj => {
          paramObj.name = this.$t(paramObj.name)
          return paramObj
        })
      })
      return tempObj
    }
  },
  async created() {
    this.plantId = this.getSelectedPlantId
    this.enableWatch = false
    await this.initialConfigList()
    await this.setIntervalWithoutDelay(this.callAllApi)
  },
  methods: {
    ...mapMutations([
      'SET_LAST_CALL_TIME',
      'SET_SHOW_SNACKBAR',
      'SET_SNACKBAR_CONTENT',
      'SET_PROGRESS_BAR'
    ]),
    async fetchWsList() {
      try {
        const response = await WSApi.wsList(this.plantId);
        const wsList = [];
        for (let value of response.data.message) {
          const list = {
            text: value.ws_name,
            value: value.ws_id,
          };
          wsList.push(list);
        }

        this.wsList = [...wsList]
      } catch(err) {
        this.wsList = []
      }
    },
    async initialConfigList() {
      await this.fetchWsList()

      const Obj = {}
      const cloneFilterOption = _.cloneDeep(this.filter);
      cloneFilterOption.forEach(child => {
        if ((child.condition && (!!child.considerStateAlways || child.condition(Obj[child.ref]))) || !child.condition) {
          if (child.type === 'select') {
            if (typeof child.value === 'function') {
              Obj[child.id] = child.value(Obj[child.ref])
            } else {
              Obj[child.id] = child.value[0]
            }
          } else if (child.type === 'datePicker') {
            let parameters = ['from', 'to']
            parameters.forEach(key => {
              if (!Obj[child.id]) { Obj[child.id] = {} }
              Obj[child.id][key] = momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD')
            })
          } else {
            Obj[child.id] = child.value
          }
        }
      })
      this.activeFilter = Obj;
      this.$emit("filter:config", cloneFilterOption);
      this.$emit('initial:active_filter', Obj)
    },
    viewChangeHandler(val) {
      this.currentView = val
    },
    updateSnackbar(payload) {
      this.SET_SHOW_SNACKBAR(true)
      this.SET_SNACKBAR_CONTENT(payload)
    },
    async callAllApi() {
      let updateTime = moment().format('MM-DD-YYYY H:mm:ss')
      this.SET_LAST_CALL_TIME(updateTime)
      await this.getWsData()
    },
    async getWsData() {
      try {
        this.SET_PROGRESS_BAR(true)
        const wsType = await WSApi.wsTypeData(this.plantId)
  
        let boxResponse, funcCall
        if (this.full_date.from === this.full_date.to) {
          funcCall = 'wsBoxData'
        } else {
          funcCall = 'wsRangeBoxData'
        }

        const boxParams = {
          wsId: this.selected_ws.value ? this.selected_ws.value : '1-1',
          plantId: this.plantId,
          dateFrom: this.full_date.from,
          dateTo: this.full_date.to
        }
        boxResponse = await WSApi[funcCall](boxParams)

        if (boxResponse.data.message.length > 0) {
          this.boxData = boxResponse.data.message[0].box
          this.$emit('boxValues', this.boxData)
        } else {
          this.$emit('boxValues', {})
        }

        const apiParams = {
          id: this.selected_ws.value ? `${this.selected_ws.value}` : `1-1`,
          plantId: this.plantId,
          type: wsType.data.message,
          dateFrom: this.full_date.from,
          dateTo: this.full_date.to
        }
        let graphResponse = await WSApi.wsData(apiParams)
        if (graphResponse.status === 200) {
          this.graphData = graphResponse.data.message.graph
          this.axis = graphResponse.data.message.axis
          this.SET_PROGRESS_BAR(false)
        } else {
          this.graphData = {}
          this.axis = []
          this.SET_PROGRESS_BAR(false)
        }

        this.$nextTick(() => {
          this.enableWatch = true
        })
      } catch (err) {
        this.SET_PROGRESS_BAR(false)
      }
    }
  },
  watch: {
    changesFromFilter: {
      handler(val) {
        this.activeFilter = val;
      },
      deep: true,
    },
    async getSelectedPlantId() {
      this.enableWatch = false
      this.plantId = this.getSelectedPlantId
      await this.initialConfigList()
      await this.callAllApi()
    },
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi)
      }
    },
    selected_ws() {
      if (this.enableWatch) {
        this.callAllApi()
      }
    },
    full_date: {
      handler() {
        if (this.enableWatch) {
          if (this.dateValidate(this.full_date.from) && this.dateValidate(this.full_date.to)) {
            this.callAllApi()
          } else {
            this.updateSnackbar({
              icon: 'error',
              message: 'Please select a valid date'
            })
          }
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.card--height {
  height: 63vh;
}

.padding-2 {
  padding: 4px;
}

.ws-datatable {
  padding: 10px 0px 0px 0px;
}
</style>
